import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Edit3, LogOut, X, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import Logo from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const TABS = ["Projects", "Services", "Industries", "Insights", "Enquiries"];

const input = "w-full bg-[#0a0a0a] border border-white/15 focus:border-[#e60000] px-3 py-2.5 text-white text-sm outline-none transition-colors";
const label = "text-[11px] tracking-[0.14em] uppercase text-white/50 mb-1.5 block";

const emptyProject = {
  title: "", role: "", organisation: "NDA / Confidential Client", engagement: "",
  location: "", sector: "", geography: "", type: "", transformation_area: "",
  resources: "", tenure: "", summary: "", business_case: "",
  activities: "", services: "", order: 0,
};
const emptyArticle = {
  title: "", category: "Business Transformation", excerpt: "", body: "",
  author: "REGERA Insights", read_time: "5 min read", cover_image: "", published: true,
};

function ProjectForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    ...emptyProject,
    ...initial,
    activities: (initial?.activities || []).join("\n"),
    services: (initial?.services || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title || !f.role || !f.location || !f.sector) {
      toast.error("Title, role, location and sector are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...f,
      order: Number(f.order) || 0,
      activities: f.activities.split("\n").map((s) => s.trim()).filter(Boolean),
      services: f.services.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (initial?.id) await api.put(`/projects/${initial.id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Project saved.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    ["title", "Title"], ["role", "Role & Title"], ["organisation", "Organisation"],
    ["engagement", "Engagement"], ["location", "Location"], ["sector", "Sector"],
    ["geography", "Geography"], ["type", "Type"], ["transformation_area", "Transformation Area"],
    ["resources", "Resources"], ["tenure", "Tenure"], ["order", "Order"],
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="project-form">
      <div className="bg-[#141414] border border-white/15 w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#141414]">
          <h3 className="font-display text-xl font-bold text-white">{initial?.id ? "Edit" : "New"} Project</h3>
          <button onClick={onClose} data-testid="project-form-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map(([k, l]) => (
              <div key={k}>
                <span className={label}>{l}</span>
                <input className={input} value={f[k]} onChange={set(k)} data-testid={`pf-${k}`} />
              </div>
            ))}
          </div>
          <div><span className={label}>Summary</span><textarea rows={2} className={input} value={f.summary} onChange={set("summary")} data-testid="pf-summary" /></div>
          <div><span className={label}>Business Case</span><textarea rows={3} className={input} value={f.business_case} onChange={set("business_case")} data-testid="pf-business_case" /></div>
          <div><span className={label}>Activities (one per line)</span><textarea rows={4} className={input} value={f.activities} onChange={set("activities")} data-testid="pf-activities" /></div>
          <div><span className={label}>Services (comma separated)</span><input className={input} value={f.services} onChange={set("services")} data-testid="pf-services" /></div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-white/70 border border-white/15">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="project-form-save" className="px-6 py-2.5 text-sm bg-[#e60000] text-white disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function ArticleForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...emptyArticle, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title || !f.category) {
      toast.error("Title and category are required.");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/articles/${initial.id}`, f);
      else await api.post("/articles", f);
      toast.success("Article saved.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="article-form">
      <div className="bg-[#141414] border border-white/15 w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#141414]">
          <h3 className="font-display text-xl font-bold text-white">{initial?.id ? "Edit" : "New"} Article</h3>
          <button onClick={onClose} data-testid="article-form-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><span className={label}>Title</span><input className={input} value={f.title} onChange={set("title")} data-testid="af-title" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><span className={label}>Category</span><input className={input} value={f.category} onChange={set("category")} data-testid="af-category" /></div>
            <div><span className={label}>Read time</span><input className={input} value={f.read_time} onChange={set("read_time")} data-testid="af-read_time" /></div>
            <div><span className={label}>Author</span><input className={input} value={f.author} onChange={set("author")} data-testid="af-author" /></div>
            <div>
              <span className={label}>Published</span>
              <select className={input} value={f.published ? "1" : "0"} onChange={(e) => setF({ ...f, published: e.target.value === "1" })} data-testid="af-published">
                <option value="1">Published</option>
                <option value="0">Draft</option>
              </select>
            </div>
          </div>
          <div><span className={label}>Excerpt</span><textarea rows={2} className={input} value={f.excerpt} onChange={set("excerpt")} data-testid="af-excerpt" /></div>
          <div><span className={label}>Body (double line-break = new paragraph)</span><textarea rows={8} className={input} value={f.body} onChange={set("body")} data-testid="af-body" /></div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-white/70 border border-white/15">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="article-form-save" className="px-6 py-2.5 text-sm bg-[#e60000] text-white disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

const SECTIONS = {
  coverage: "Industry coverage (grouped lists)",
  emerging: "Services, commerce & emerging needs (tiles)",
};

// Services and Industries share one form; `kind` picks the fields.
function CatalogForm({ kind, initial, groups, nextOrder, onClose, onSaved }) {
  const isService = kind === "services";
  const blank = isService
    ? { title: "", group: groups[0] || "", area: "", text: "", points: "", published: true }
    : { name: "", section: "coverage", group: groups[0] || "", published: true };
  const [f, setF] = useState({ ...blank, ...initial, ...(isService ? { points: (initial?.points || []).join("\n") } : {}) });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const noun = isService ? "Service" : "Industry";

  const save = async () => {
    if (isService ? !f.title.trim() || !f.group.trim() : !f.name.trim()) {
      toast.error(isService ? "Title and group are required." : "Name is required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...f,
      order: initial?.id ? initial.order : nextOrder,
      ...(isService ? { points: f.points.split("\n").map((x) => x.trim()).filter(Boolean) } : {}),
    };
    try {
      if (initial?.id) await api.put(`/${kind}/${initial.id}`, payload);
      else await api.post(`/${kind}`, payload);
      toast.success(`${noun} saved.`);
      onSaved();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid={`${kind}-form`}>
      <div className="bg-[#141414] border border-white/15 w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#141414]">
          <h3 className="font-display text-xl font-bold text-white">{initial?.id ? "Edit" : "New"} {noun}</h3>
          <button onClick={onClose} data-testid={`${kind}-form-close`}><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <datalist id={`${kind}-groups`}>{groups.map((g) => <option key={g} value={g} />)}</datalist>
        <div className="p-5 space-y-4">
          {isService ? (
            <>
              <div><span className={label}>Title</span><input className={input} value={f.title} onChange={set("title")} data-testid="sf-title" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><span className={label}>Group (heading on the Services page)</span><input list="services-groups" className={input} value={f.group} onChange={set("group")} data-testid="sf-group" /></div>
                <div><span className={label}>Contact form option (optional)</span><input className={input} value={f.area} onChange={set("area")} placeholder="Defaults to the title" data-testid="sf-area" /></div>
              </div>
              <div><span className={label}>Description</span><textarea rows={3} className={input} value={f.text} onChange={set("text")} data-testid="sf-text" /></div>
              <div><span className={label}>Key points (one per line)</span><textarea rows={6} className={input} value={f.points} onChange={set("points")} data-testid="sf-points" /></div>
            </>
          ) : (
            <>
              <div><span className={label}>Industry name</span><input className={input} value={f.name} onChange={set("name")} data-testid="if-name" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <span className={label}>Section of the Industries page</span>
                  <select className={input} value={f.section} onChange={set("section")} data-testid="if-section">
                    {Object.entries(SECTIONS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div><span className={label}>Group (heading)</span><input list="industries-groups" className={input} value={f.group} onChange={set("group")} data-testid="if-group" /></div>
              </div>
            </>
          )}
          <div className="sm:w-1/2">
            <span className={label}>Status</span>
            <select className={input} value={f.published ? "1" : "0"} onChange={(e) => setF({ ...f, published: e.target.value === "1" })} data-testid={`${kind}-form-published`}>
              <option value="1">Published</option>
              <option value="0">Hidden (draft)</option>
            </select>
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-white/70 border border-white/15">Cancel</button>
          <button onClick={save} disabled={saving} data-testid={`${kind}-form-save`} className="px-6 py-2.5 text-sm bg-[#e60000] text-white disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function CatalogList({ kind, items, onEdit, onChanged, onDelete }) {
  const isService = kind === "services";
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const ids = items.map((x) => x.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      await api.post(`/${kind}/reorder`, { ids });
      onChanged();
    } catch {
      toast.error("Reorder failed.");
    }
  };
  const togglePublish = async (x) => {
    try {
      await api.put(`/${kind}/${x.id}`, { ...x, published: !x.published });
      toast.success(x.published ? "Hidden from the website." : "Published.");
      onChanged();
    } catch {
      toast.error("Update failed.");
    }
  };
  const iconBtn = "p-2 text-white/60 hover:text-white disabled:opacity-20 disabled:hover:text-white/60";
  return (
    <div className="border border-white/10 divide-y divide-white/10" data-testid={`${kind}-list`}>
      {items.length === 0 && <div className="p-10 text-center text-white/40">Nothing here yet.</div>}
      {items.map((x, i) => (
        <div key={x.id} className="flex items-center justify-between gap-4 p-4 hover:bg-[#141414]" data-testid={`${kind}-row`}>
          <div className="min-w-0 flex items-start gap-4">
            <span className="font-display text-sm font-bold text-[#e60000] w-6 shrink-0 pt-0.5">{String(i + 1).padStart(2, "0")}</span>
            <div className="min-w-0">
              <p className="text-white font-medium truncate">
                {isService ? x.title : x.name} {!x.published && <span className="text-[10px] text-[#e60000] ml-2">HIDDEN</span>}
              </p>
              <p className="text-xs text-white/40 truncate">{isService ? x.group : `${x.group || "—"} · ${x.section === "emerging" ? "tiles" : "lists"}`}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`${kind}-up-${i}`} className={iconBtn}><ChevronUp className="h-4 w-4" /></button>
            <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down" data-testid={`${kind}-down-${i}`} className={iconBtn}><ChevronDown className="h-4 w-4" /></button>
            <button onClick={() => togglePublish(x)} aria-label={x.published ? "Hide" : "Publish"} title={x.published ? "Hide from the website" : "Publish"} data-testid={`${kind}-publish-${i}`} className={iconBtn}>
              {x.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-[#e60000]" />}
            </button>
            <button onClick={() => onEdit(x)} aria-label="Edit" data-testid={`${kind}-edit-${i}`} className={iconBtn}><Edit3 className="h-4 w-4" /></button>
            <button onClick={() => onDelete(x.id)} aria-label="Delete" data-testid={`${kind}-delete-${i}`} className="p-2 text-white/60 hover:text-[#e60000]"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Projects");
  const [projects, setProjects] = useState([]);
  const [articles, setArticles] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [services, setServices] = useState([]);
  const [industries, setIndustries] = useState([]);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null); // {type, data}

  useEffect(() => {
    document.title = "Dashboard — REGERA";
    if (!loading && !user) nav("/admin/login");
  }, [user, loading, nav]);

  const load = useCallback(async () => {
    try {
      const [p, a, e, s, ind] = await Promise.all([
        api.get("/projects"),
        api.get("/articles", { params: { include_drafts: true } }),
        api.get("/enquiries"),
        api.get("/admin/services"),
        api.get("/admin/industries"),
      ]);
      setProjects(p.data);
      setArticles(a.data);
      setEnquiries(e.data);
      setServices(s.data);
      setIndustries(ind.data);
      // the public pages in this browser pick up edits straight away
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["industries"] });
    } catch (err) {
      /* handled by interceptors */
    }
  }, [queryClient]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const del = async (kind, id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/${kind}/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (loading || !user) return <div className="min-h-screen bg-[#0a0a0a]" />;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-dashboard">
      <div className="grain-overlay" aria-hidden />
      <header className="border-b border-white/10 sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-xl z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Logo size="admin" />
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/40 hidden sm:block">{user.email}</span>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-[#e60000]">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex gap-2 mb-8 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`px-5 py-3 text-sm tracking-wide -mb-px border-b-2 transition-colors ${
                tab === t ? "border-[#e60000] text-white" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              {t} <span className="text-white/30">({{ Projects: projects, Services: services, Industries: industries, Insights: articles, Enquiries: enquiries }[t].length})</span>
            </button>
          ))}
        </div>

        {tab === "Projects" && (
          <div>
            <button onClick={() => setEditing({ type: "project", data: null })} data-testid="add-project" className="mb-6 inline-flex items-center gap-2 bg-[#e60000] text-white px-5 py-2.5 text-sm">
              <Plus className="h-4 w-4" /> New Project
            </button>
            <div className="border border-white/10 divide-y divide-white/10">
              {projects.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-4 p-4 hover:bg-[#141414]">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{p.title}</p>
                    <p className="text-xs text-white/40">{p.sector} · {p.role}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setEditing({ type: "project", data: p })} data-testid={`edit-project-${p.slug}`} className="p-2 text-white/60 hover:text-white"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => del("projects", p.id)} data-testid={`delete-project-${p.slug}`} className="p-2 text-white/60 hover:text-[#e60000]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(tab === "Services" || tab === "Industries") && (() => {
          const kind = tab.toLowerCase();
          const items = kind === "services" ? services : industries;
          return (
            <div>
              <div className="mb-6 flex flex-wrap items-center gap-4 justify-between">
                <button onClick={() => setEditing({ type: kind, data: null })} data-testid={`add-${kind}`} className="inline-flex items-center gap-2 bg-[#e60000] text-white px-5 py-2.5 text-sm">
                  <Plus className="h-4 w-4" /> {kind === "services" ? "New Service" : "New Industry"}
                </button>
                <p className="text-xs text-white/40">Changes appear on the website immediately. Use the arrows to set the display order.</p>
              </div>
              <CatalogList kind={kind} items={items} onChanged={load} onEdit={(x) => setEditing({ type: kind, data: x })} onDelete={(id) => del(kind, id)} />
            </div>
          );
        })()}

        {tab === "Insights" && (
          <div>
            <button onClick={() => setEditing({ type: "article", data: null })} data-testid="add-article" className="mb-6 inline-flex items-center gap-2 bg-[#e60000] text-white px-5 py-2.5 text-sm">
              <Plus className="h-4 w-4" /> New Article
            </button>
            <div className="border border-white/10 divide-y divide-white/10">
              {articles.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4 p-4 hover:bg-[#141414]">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{a.title} {!a.published && <span className="text-[10px] text-[#e60000] ml-2">DRAFT</span>}</p>
                    <p className="text-xs text-white/40">{a.category}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setEditing({ type: "article", data: a })} data-testid={`edit-article-${a.slug}`} className="p-2 text-white/60 hover:text-white"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => del("articles", a.id)} data-testid={`delete-article-${a.slug}`} className="p-2 text-white/60 hover:text-[#e60000]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Enquiries" && (
          <div className="border border-white/10 divide-y divide-white/10" data-testid="enquiries-list">
            {enquiries.length === 0 && <div className="p-10 text-center text-white/40">No enquiries yet.</div>}
            {enquiries.map((e) => (
              <div key={e.id} className="p-5">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-white font-medium">{e.name} <span className="text-white/40 text-sm">· {e.organisation || "—"}</span></p>
                  <span className="text-xs text-white/30">{new Date(e.created_at).toLocaleString()}</span>
                </div>
                <p className="text-xs text-[#e60000] mt-1">{e.help_with || "General"} · {e.email} · {e.country || "—"}</p>
                <p className="text-sm text-white/70 mt-3 whitespace-pre-wrap">{e.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing?.type === "project" && (
        <ProjectForm initial={editing.data} onClose={() => setEditing(null)} onSaved={load} />
      )}
      {(editing?.type === "services" || editing?.type === "industries") && (
        <CatalogForm
          kind={editing.type}
          initial={editing.data}
          groups={[...new Set((editing.type === "services" ? services : industries).map((x) => x.group).filter(Boolean))]}
          nextOrder={(editing.type === "services" ? services : industries).reduce((m, x) => Math.max(m, x.order ?? 0), -1) + 1}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
      {editing?.type === "article" && (
        <ArticleForm initial={editing.data} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
